// Numbers domain mappers.
const getPlaceholderValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return String(value);
};

const formatMonthlyFee = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const stringValue = String(value).trim();
  if (!stringValue) {
    return "-";
  }

  const numericValue = Number(stringValue);
  if (Number.isFinite(numericValue)) {
    return `$${numericValue.toFixed(2)}`;
  }

  return stringValue;
};

export const buildGreetingFileNameFromPhoneNumber = (phoneNumber) => {
  const digits = String(phoneNumber || "").replace(/\D/g, "");
  const normalizedDigits =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

  return normalizedDigits ? `${normalizedDigits}.mp3` : "";
};

export const isLikelyTollFreeNumber = (phoneNumber = "", friendlyName = "") => {
  const normalized = String(phoneNumber).replace(/\D/g, "");
  const friendlyLower = String(friendlyName).toLowerCase();

  if (friendlyLower.includes("toll-free") || friendlyLower.includes("toll free")) {
    return true;
  }

  const tollFreePrefixes = ["800", "833", "844", "855", "866", "877", "888", "822"];

  if (normalized.length === 11 && normalized.startsWith("1")) {
    return tollFreePrefixes.some((prefix) => normalized.slice(1, 4) === prefix);
  }

  if (normalized.length === 10) {
    return tollFreePrefixes.some((prefix) => normalized.slice(0, 3) === prefix);
  }

  return false;
};

export const mapAvailableNumber = (item, index) => {
  const capabilities = item?.capabilities || {};
  const phoneNumber = getPlaceholderValue(item?.phone_number || item?.phoneNumber);
  const friendlyName = getPlaceholderValue(item?.friendly_name || item?.friendlyName);
  const phoneType = "Local";

  return {
    id: item?.id || `${phoneNumber}-${index}`,
    number: phoneNumber,
    type: phoneType,
    monthlyFee: formatMonthlyFee(
      item?.monthly_price ?? item?.monthlyPrice ?? item?.monthly_fee ?? item?.monthlyFee,
    ),
    monthlyPrice: getPlaceholderValue(
      item?.monthly_price ?? item?.monthlyPrice ?? item?.monthly_fee ?? item?.monthlyFee,
    ),
    phoneNumber,
    friendlyName,
    phoneType,
    voiceCapabilities: Boolean(
      capabilities.voice ?? item?.voice_capabilities ?? item?.voiceCapabilities,
    ),
    smsCapabilities: Boolean(
      capabilities.SMS ??
        capabilities.sms ??
        item?.sms_capabilities ??
        item?.smsCapabilities,
    ),
    mmsCapabilities: Boolean(
      capabilities.MMS ??
        capabilities.mms ??
        item?.mms_capabilities ??
        item?.mmsCapabilities,
    ),
  };
};

export const mapPurchasedNumber = (item, index) => {
  const greetingFile = getPlaceholderValue(
    item?.greetings_file_name || item?.greeting_file || item?.greetingFile,
  );
  const greetingUrl =
    item?.greetings_url ||
    item?.greeting_url ||
    item?.greetingsUrl ||
    item?.greetingUrl ||
    "";

  return {
    id: item?.id || `${item?.phone_number || item?.phoneNumber || "number"}-${index}`,
    phoneNumber: getPlaceholderValue(item?.phone_number || item?.phoneNumber),
    nickname: getPlaceholderValue(
      item?.friendly_name || item?.friendlyName || item?.nickname,
    ),
    greetingFile,
    greetingUrl,
    hasGreeting: greetingFile !== "-" || Boolean(greetingUrl),
  };
};

export const extractGreetingUrl = (response) => {
  const data = response?.data || response;
  return (
    data?.greetings_url ||
    data?.greeting_url ||
    data?.greetingsUrl ||
    data?.greetingUrl ||
    ""
  );
};

export const extractGreetingUploadUrl = (response) =>
  response?.greetings_upload_url ||
  response?.greeting_upload_url ||
  response?.data?.greetings_upload_url ||
  response?.data?.greeting_upload_url ||
  "";

export const extractGreetingFileName = (greetingUrl) => {
  if (!greetingUrl) return "-";
  const sanitized = String(greetingUrl).split("?")[0];
  const name = sanitized.split("/").pop();
  return name || "-";
};

export const getAvailableListFromResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response?.phone_numbers)) {
    return response.phone_numbers;
  }
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  return [];
};

export const getPurchasedListFromResponse = (response) => {
  if (Array.isArray(response?.phone_numbers)) {
    return response.phone_numbers;
  }
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  if (Array.isArray(response)) {
    return response;
  }
  return [];
};

export const getDynamicPageSize = (response, listLength = 0) => {
  const rawLimit = response?.limit ?? response?.page_size ?? response?.pageSize;
  const parsedLimit = Number(rawLimit);

  if (Number.isFinite(parsedLimit) && parsedLimit > 0) {
    return parsedLimit;
  }

  return 10;
};

export const toSearchPayload = (searchState) => {
  const patternMap = {
    first: "starts_with",
    last: "ends_with",
    anywhere: "contains",
  };

  return {
    country_code: searchState.selectedCountry,
    search_type: "number",
    search_pattern: patternMap[searchState.matchTo] || "contains",
    search_string: (searchState.searchDigits || "").trim(),
  };
};

export const toBuyNumberPayload = (number, clientId) => ({
  client_id: clientId,
  phone_number: number.phoneNumber,
  phone_type: "local",
  friendly_name:
    number.friendlyName === "-" ? number.phoneNumber : number.friendlyName,
  voice_capabilities: Boolean(number.voiceCapabilities),
  sms_capabilities: Boolean(number.smsCapabilities),
  mms_capabilities: Boolean(number.mmsCapabilities),
});

export const getNumberManagementErrorMessage = (response, fallbackMessage) => {
  const rawMessage = (response?.error?.message || "").toString().trim();
  const loweredMessage = rawMessage.toLowerCase();

  const isGenericServerError =
    !rawMessage ||
    loweredMessage.includes("internal server error") ||
    loweredMessage.includes("server error") ||
    loweredMessage.includes("unexpected error");

  if (isGenericServerError) {
    return fallbackMessage;
  }

  return rawMessage;
};
